/**
 * PersonalCtaSection.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * [배경] B2C 개인고객 전용 CTA 섹션
 * "서랍 속 기기, 오늘 현금으로 바꾸세요" - 개인 고객 톤의 최종 전환 유도
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React from 'react';
import {
  Box, Typography, Button, Stack, Chip,
} from '@mui/material';
import { ArrowForward, CheckCircle } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useChannelPath } from '../../../../contexts/ChannelContext';

const benefits = ['수수료 0원', '무료 택배 수거', '안전한 데이터 삭제'];

const PersonalCtaSection: React.FC = () => {
  const channelPath = useChannelPath();

  return (
    <Box
      sx={{
        py: { xs: 5, md: 14 },
        position: 'relative',
        overflow: 'hidden',
        background: `
          linear-gradient(135deg, 
            rgba(14, 165, 233, 0.03) 0%, 
            rgba(255, 255, 255, 0.98) 30%, 
            rgba(6, 182, 212, 0.03) 70%, 
            rgba(255, 255, 255, 0.98) 100%)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -100,
          left: -100,
          right: -100,
          bottom: -100,
          background: 'conic-gradient(from 180deg at 50% 50%, rgba(14,165,233,0.03) 0deg, rgba(6,182,212,0.03) 120deg, rgba(139,92,246,0.02) 240deg, rgba(14,165,233,0.03) 360deg)',
          animation: 'ctaRotate 25s linear infinite',
          zIndex: 0,
        },
        '@keyframes ctaRotate': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      }}
    >
      <Box sx={{ maxWidth: { xs: '100%', md: '50%' }, mx: 'auto', px: { xs: 2, md: 3 }, position: 'relative', zIndex: 1, textAlign: 'center' }}>
        {/* 메인 타이틀 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.3rem', sm: '2rem', md: '2.4rem' },
              letterSpacing: '-0.02em',
              color: '#0f172a',
              mb: 2,
            }}
          >
            서랍 속 기기,<br />
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              오늘 현금으로
            </Box>{' '}
            바꾸세요
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="body1"
            sx={{
              color: '#64748b',
              mb: { xs: 3, md: 5 },
              fontSize: { xs: '0.85rem', md: '1.1rem' },
              lineHeight: 1.6,
            }}
          >
            회원가입 없이 3분이면 신청 완료
          </Typography>
        </motion.div>

        {/* CTA 버튼 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
        >
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            href={channelPath('/apply')}
            sx={{
              borderRadius: 50,
              px: { xs: 4, md: 8 },
              py: { xs: 1.4, md: 2.2 },
              fontWeight: 700,
              fontSize: { xs: '1rem', md: '1.15rem' },
              background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 50%, #0ea5e9 100%)',
              color: '#fff',
              textTransform: 'none',
              boxShadow: '0 16px 40px rgba(14, 165, 233, 0.3), 0 8px 20px rgba(6, 182, 212, 0.2)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.6s ease',
              },
              '&:hover': {
                background: 'linear-gradient(135deg, #0284c7 0%, #0891b2 50%, #0284c7 100%)',
                boxShadow: '0 20px 50px rgba(14, 165, 233, 0.4), 0 12px 30px rgba(6, 182, 212, 0.3)',
                '&::before': { left: '100%' },
              },
              transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)',
            }}
          >
            지금 매입 신청하기
          </Button>
        </motion.div>

        {/* 하단 혜택 뱃지 */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="center"
            sx={{ mt: 4, flexWrap: 'wrap', gap: 1 }}
          >
            {benefits.map((text, i) => (
              <Chip
                key={i}
                icon={<CheckCircle sx={{ fontSize: 16 }} />}
                label={text}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: '#0ea5e9',
                  bgcolor: 'rgba(14, 165, 233, 0.06)',
                  border: '1px solid rgba(14, 165, 233, 0.15)',
                  '& .MuiChip-icon': { color: '#0ea5e9' },
                }}
              />
            ))}
          </Stack>
        </motion.div>
      </Box>
    </Box>
  );
};

export default PersonalCtaSection;
